import React, { useState, useContext } from 'react';
import { AuthContext } from '../../../../../context/AuthContext';
import SafetySummaryModal from './SafetySummaryModal';
import FileAttachment from '../../../../../components/FileAttachment/FileAttachment';

function SafetySummaryTab({ assessmentId, summaryData, setSummaryData, isLocked, onSave, projectInfo }) {
  const { user } = useContext(AuthContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('WRITE'); // 'WRITE' or 'DETAIL'

  const userName = user?.UserName || user?.name || '담당자';
  const userPosition = user?.position || '';
  const userDisplayName = userPosition ? `${userName} ${userPosition}` : userName;

  const getTodayStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const hasContent = Boolean(summaryData?.conclusion && summaryData.conclusion.trim() !== '');

  // 권한 판단 (admin, 등록자 본인, 안전관리자 1/2, 기안자)
  const hasEditPermission = () => {
    if (user?.role === 'admin') return true;

    const currentUserId = user?.userid || user?.userId || user?.id;
    const currentUserName = user?.UserName || user?.name;

    // 작성자(등록자) 체크
    if (summaryData?.writer && (summaryData.writer.includes(currentUserName) || summaryData.writer === userDisplayName)) return true;
    if (summaryData?.created_by && (summaryData.created_by === currentUserId || summaryData.created_by === currentUserName)) return true;

    // 프로젝트 기안자 & 안전관리자 (1, 2차 담당자)
    if (projectInfo) {
      if (projectInfo.writeId && (projectInfo.writeId === currentUserId || projectInfo.writeId === currentUserName)) return true;
      if (projectInfo.writerId && (projectInfo.writerId === currentUserId || projectInfo.writerId === currentUserName)) return true;
      if (projectInfo.managerId && (projectInfo.managerId === currentUserId || projectInfo.managerId === currentUserName)) return true;
      if (projectInfo.managerId2 && (projectInfo.managerId2 === currentUserId || projectInfo.managerId2 === currentUserName)) return true;
      if (projectInfo.safetyManager1 && (projectInfo.safetyManager1 === currentUserId || projectInfo.safetyManager1 === currentUserName)) return true;
      if (projectInfo.safetyManager2 && (projectInfo.safetyManager2 === currentUserId || projectInfo.safetyManager2 === currentUserName)) return true;
      if (projectInfo.managerName && String(projectInfo.managerName).includes(currentUserName)) return true;
      if (projectInfo.safetyManagers && String(projectInfo.safetyManagers).includes(currentUserName)) return true;
    }

    return false;
  };

  // 신규 작성 오픈
  const handleOpenWrite = () => {
    setModalMode('WRITE');
    setIsModalOpen(true);
  };

  // 상세보기 오픈
  const handleOpenDetail = () => {
    setModalMode('DETAIL');
    setIsModalOpen(true);
  };

  // 수정 모드로 전환
  const handleStartEdit = () => {
    setModalMode('WRITE');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveModal = (data) => {
    const updatedSummary = {
      ...summaryData,
      writer: data.writer,
      writeDate: data.writeDate,
      conclusion: data.conclusion,
      files: data.files || []
    };
    setSummaryData(updatedSummary);
    setIsModalOpen(false);
    if (onSave) {
      onSave('summary', updatedSummary);
    }
  };

  const handleDeleteModal = () => {
    if (window.confirm('작성된 종합 총평을 삭제하시겠습니까?')) {
      const clearedSummary = {
        ...summaryData,
        conclusion: '',
        files: []
      };
      setSummaryData(clearedSummary);
      setIsModalOpen(false);
      if (onSave) {
        onSave('summary_delete', clearedSummary);
      }
    }
  };

  return (
    <div className="sf-card" style={{ padding: 0, marginBottom: '20px' }}>
      {/* 컨트롤 패널 (sf-card-header 헤더) */}
      <div
        className="sf-card-header"
        style={{
          padding: '16px 22px',
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: '16px',
          overflow: 'hidden'
        }}
      >
        {/* 타이틀 영역 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fas fa-file-alt" style={{ color: '#2563eb', fontSize: '1.1rem' }}></i>
          <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
            위험성평가 종합 총평
          </h5>
        </div>

        {/* 오른쪽: 상응하는 작성/수정 버튼 */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {(!isLocked || user?.role === 'admin') && hasEditPermission() && (
            <button
              onClick={handleOpenWrite}
              style={{
                height: '38px',
                padding: '0 18px',
                background: hasContent ? '#0284c7' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.88rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
              }}
            >
              <i className={hasContent ? 'fas fa-edit' : 'fas fa-pen'}></i>
              {hasContent ? '총평 수정' : '총평 작성'}
            </button>
          )}
        </div>
      </div>

      {/* 본문 콘텐츠 영역 (카드 상세 뷰) */}
      <div style={{ padding: '20px 22px' }}>
        {!hasContent ? (
          /* 미작성 상태 */
          <div
            className="sf-empty"
            style={{
              padding: '48px 24px',
              border: '2px dashed #e2e8f0',
              borderRadius: '8px',
              background: '#f8fafc',
              textAlign: 'center'
            }}
          >
            <div className="sf-empty-icon" style={{ color: '#94a3b8', fontSize: '2rem', marginBottom: '12px' }}>
              <i className="fas fa-folder-open"></i>
            </div>
            <div className="sf-empty-text" style={{ fontSize: '0.92rem', color: '#64748b', fontWeight: 500, marginBottom: '16px' }}>
              등록된 위험성평가 종합 총평이 없습니다.
            </div>
            {(!isLocked || user?.role === 'admin') && hasEditPermission() && (
              <button
                onClick={handleOpenWrite}
                style={{
                  height: '36px',
                  padding: '0 16px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-plus me-1"></i> 종합 총평 작성
              </button>
            )}
          </div>
        ) : (
          /* 작성 완료 상태 (프리미엄 카드 뷰) */
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              background: '#ffffff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
              overflow: 'hidden'
            }}
          >
            {/* 메타 헤더 정보 바 */}
            <div
              style={{
                background: '#f8fafc',
                padding: '12px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justify: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}
            >
              {/* 왼쪽 정렬: 작성자 및 작성일 (아이콘 & 구분선) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', color: '#475569' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <i className="fas fa-user" style={{ color: '#94a3b8', marginRight: '6px' }}></i>
                  <span style={{ fontWeight: 600, color: '#1e293b', marginRight: '4px' }}>작성자:</span>
                  <span>{summaryData.writer || userDisplayName}</span>
                </div>
                <span style={{ color: '#cbd5e1', fontWeight: 300 }}>|</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <i className="far fa-calendar-alt" style={{ color: '#94a3b8', marginRight: '6px' }}></i>
                  <span style={{ fontWeight: 600, color: '#1e293b', marginRight: '4px' }}>작성일:</span>
                  <span>{summaryData.writeDate || getTodayStr()}</span>
                </div>
              </div>

              {/* 오른쪽 정렬: 맨 오른쪽 첨부파일 배지 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                {summaryData.files && summaryData.files.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '4px 10px', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                    <i className="fas fa-paperclip"></i>
                    <span>첨부파일 {summaryData.files.length}개</span>
                  </div>
                )}
              </div>
            </div>

            {/* 에디터 작성 본문 (HTML 미리보기 렌더링) */}
            <div
              onClick={handleOpenDetail}
              style={{
                padding: '20px 24px',
                minHeight: '80px',
                fontSize: '10pt',
                lineHeight: '1.2',
                color: '#1e293b',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              className="sf-summary-content-view"
              title="클릭하여 상세 보기"
            >
              <style>{`
                .sf-summary-content-view p { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
                .sf-summary-content-view { font-size: 10pt; line-height: 1.2; }
              `}</style>
              <div
                dangerouslySetInnerHTML={{ __html: summaryData.conclusion }}
                style={{ wordBreak: 'break-word' }}
              />
            </div>

            {/* 첨부파일 영역 (첨부파일이 존재하는 경우 하단에 렌더링) */}
            {summaryData.files && summaryData.files.length > 0 && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#fafafa' }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#475569', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="fas fa-paperclip" style={{ color: '#2563eb' }}></i> 첨부파일 ({summaryData.files.length}개)
                </div>
                <FileAttachment files={summaryData.files} setFiles={() => {}} mode="view" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 회의록 모달 팝업 연결 */}
      <SafetySummaryModal
        show={isModalOpen}
        mode={modalMode}
        onClose={handleCloseModal}
        onSave={handleSaveModal}
        onDelete={handleDeleteModal}
        onStartEdit={handleStartEdit}
        initialData={summaryData}
        isLocked={isLocked}
        canEdit={(!isLocked || user?.role === 'admin') && hasEditPermission()}
      />
    </div>
  );
}

export default SafetySummaryTab;
